import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, List, LayoutGrid, Filter, User, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useActivityStore } from '@/store/activityStore';
import { useActivityTypeStore } from '@/store/activityTypeStore';
import { useAuthStore } from '@/store/authStore';
import ActivityCard from '@/components/activities/ActivityCard';
import ActivityKanban from '@/components/activities/ActivityKanban';
import ActivityFileDropzone from '@/components/activities/ActivityFileDropzone';
import ActivityFormDialog from '@/components/activities/ActivityFormDialog';
import PracticeActivityGroups from '@/components/activities/PracticeActivityGroups';
import type { PracticeGroup } from '@/components/activities/practiceActivityGroupTypes';
import { Activity } from '@/services/activityService';
import { apiClient } from '@/utils/api';
import { toast } from 'sonner';

type ViewMode = 'list' | 'kanban' | 'grouped';
type FilterMode = 'mine' | 'unassigned' | 'open' | 'completed' | 'all';

interface AssignableUser {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

export default function ActivitiesPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedPractices, setExpandedPractices] = useState<Set<string>>(new Set());
  const [filterMode, setFilterMode] = useState<FilterMode>('mine');
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTypeCodes, setSelectedTypeCodes] = useState<Set<string>>(new Set());
  const [dueDateRange, setDueDateRange] = useState<'all' | '30' | '60' | '90'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAssignUserId, setBulkAssignUserId] = useState('');
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [actionActivity, setActionActivity] = useState<Activity | null>(null);
  const [actionMode, setActionMode] = useState<'file' | 'form' | null>(null);
  const [completedFilter, setCompletedFilter] = useState<'mine' | 'all'>('mine');

  const {
    activities,
    loading,
    error,
    pagination,
    fetchActivities,
    updateActivity,
    updateActivityStatus,
    completeActivity,
    bulkAssign,
    clearError,
  } = useActivityStore();

  const { activityTypes, fetchActivityTypes } = useActivityTypeStore();

  useEffect(() => {
    fetchActivityTypes(true);
  }, [fetchActivityTypes]);

  useEffect(() => {
    apiClient.get('/users/assignable')
      .then((res) => setUsers(res.data?.data || []))
      .catch(() => {
        if (user) {
          setUsers([{
            id: user.id,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
          }]);
        }
      });
  }, [user]);

  useEffect(() => {
    const params: any = {
      page: currentPage,
      limit: itemsPerPage,
    };

    if (viewMode === 'grouped') {
      params.groupByPractice = true;
    }

    if (searchTerm) params.search = searchTerm;

    if (filterMode === 'mine' && user) {
      params.userId = user.id;
      params.status = 'ACTIVE';
    } else if (filterMode === 'unassigned') {
      params.userId = 'null' as any;
    } else if (filterMode === 'open') {
      params.status = 'OPEN';
    } else if (filterMode === 'completed') {
      params.status = 'COMPLETED';
      if (completedFilter === 'mine' && user) params.userId = user.id;
    }

    if (selectedTypeCodes.size > 0) {
      params.type = Array.from(selectedTypeCodes).join(',');
    }

    if (dueDateRange !== 'all') {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - parseInt(dueDateRange));
      params.dueDateStart = start.toISOString();
    }

    fetchActivities(params);
  }, [searchTerm, filterMode, completedFilter, user, currentPage, itemsPerPage, selectedTypeCodes, dueDateRange, viewMode, fetchActivities]);

  useEffect(() => {
    if (pagination && pagination.page !== currentPage) {
      setCurrentPage(pagination.page);
    }
  }, [pagination?.page]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterMode, completedFilter, selectedTypeCodes, dueDateRange]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleActivityClick = (activity: Activity) => {
    navigate(`/activities/${activity.id}`);
  };

  const buildFetchParams = () => {
    const params: any = { page: currentPage, limit: itemsPerPage };
    if (viewMode === 'grouped') params.groupByPractice = true;
    if (searchTerm) params.search = searchTerm;
    if (filterMode === 'mine' && user) {
      params.userId = user.id;
      params.status = 'ACTIVE';
    } else if (filterMode === 'unassigned') params.userId = 'null' as any;
    else if (filterMode === 'open') params.status = 'OPEN';
    else if (filterMode === 'completed') {
      params.status = 'COMPLETED';
      if (completedFilter === 'mine' && user) params.userId = user.id;
    }
    if (selectedTypeCodes.size > 0) params.type = Array.from(selectedTypeCodes).join(',');
    if (dueDateRange !== 'all') {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - parseInt(dueDateRange));
      params.dueDateStart = start.toISOString();
    }
    return params;
  };

  const handleAssign = async (activityId: string, userId: string) => {
    try {
      await updateActivity(activityId, {
        userId: userId || undefined,
      });
      if (filterMode === 'unassigned') {
        fetchActivities(buildFetchParams());
      }
    } catch {
      // Error already handled in store
    }
  };

  const handleStatusChange = async (activityId: string, status: Activity['status']) => {
    try {
      await updateActivityStatus(activityId, status);
    } catch {
      // Error already handled in store
    }
  };

  const handleSelectChange = (activityId: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(activityId);
      else next.delete(activityId);
      return next;
    });
  };

  const handleBulkAssign = async () => {
    if (selectedIds.size === 0 || !bulkAssignUserId) return;
    try {
      await bulkAssign(Array.from(selectedIds), bulkAssignUserId);
      setSelectedIds(new Set());
      setBulkAssignUserId('');
      fetchActivities(buildFetchParams());
    } catch {
      // Error handled in store
    }
  };

  const showCheckboxes = filterMode === 'unassigned' || filterMode === 'all';

  const groupedByPractice = useMemo<PracticeGroup[]>(() => {
    const map = new Map<string, PracticeGroup>();
    const order: string[] = [];
    for (const a of activities) {
      const pid = a.practiceId;
      if (!map.has(pid)) {
        order.push(pid);
        map.set(pid, {
          practiceId: pid,
          practiceNumber: a.practice?.practiceNumber ?? null,
          title: a.practice?.title || 'Senza titolo',
          clientName: a.practice?.client
            ? `${a.practice.client.firstName} ${a.practice.client.lastName}`
            : '',
          typeName: a.practice?.type?.name || '',
          activities: [],
        });
      }
      map.get(pid)!.activities.push(a);
    }
    return order.map((pid) => map.get(pid)!);
  }, [activities]);

  const togglePracticeExpand = (pid: string) => {
    setExpandedPractices(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const toggleTypeFilterCode = (code: string) => {
    setSelectedTypeCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-gray-900">Attività</h1>
            <div className="flex items-center gap-2">
              <Button
                variant={filterMode === 'mine' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterMode('mine')}
              >
                LE MIE
              </Button>
              <Button
                variant={filterMode === 'unassigned' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterMode('unassigned')}
              >
                DA ASSEGNARE
              </Button>
              <Button
                variant={filterMode === 'open' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterMode('open')}
              >
                APERTE
              </Button>
              <Button
                variant={filterMode === 'completed' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterMode('completed')}
              >
                COMPLETATE
              </Button>
              {filterMode === 'completed' && (
                <div className="flex gap-1 ml-1 border-l pl-2">
                  <Button
                    variant={completedFilter === 'mine' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setCompletedFilter('mine')}
                  >
                    Le mie
                  </Button>
                  <Button
                    variant={completedFilter === 'all' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setCompletedFilter('all')}
                  >
                    Tutte
                  </Button>
                </div>
              )}
              <Button
                variant={filterMode === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterMode('all')}
              >
                TUTTE
              </Button>
            </div>
          </div>
          <p className="text-gray-500 mt-1">
            Gestisci tutte le attività delle pratiche
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border rounded-lg">
            <Button
              variant={viewMode === 'grouped' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grouped')}
              title="Per pratica"
            >
              <FolderOpen className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              title="Lista"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
              title="Kanban"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={() => navigate('/activity-types')}>
            <Plus className="h-4 w-4 mr-2" />
            NUOVO TIPO ATTIVITÀ
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Cerca attività..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  Tipo: {selectedTypeCodes.size === 0 ? 'Tutti' : `${selectedTypeCodes.size} selezionati`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="start">
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {activityTypes.map((at) => (
                    <label key={at.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTypeCodes.has(at.code)}
                        onChange={() => toggleTypeFilterCode(at.code)}
                        className="h-4 w-4 rounded"
                      />
                      <span className="text-sm">{at.name} ({at.code})</span>
                    </label>
                  ))}
                  {activityTypes.length === 0 && (
                    <p className="text-sm text-gray-500">Nessun tipo attività</p>
                  )}
                </div>
                {selectedTypeCodes.size > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => setSelectedTypeCodes(new Set())}
                  >
                    Cancella filtro
                  </Button>
                )}
              </PopoverContent>
            </Popover>
            <Select value={dueDateRange} onValueChange={(v: any) => setDueDateRange(v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Scadenza" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le scadenze</SelectItem>
                <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                <SelectItem value="60">Ultimi 60 giorni</SelectItem>
                <SelectItem value="90">Ultimi 90 giorni</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {selectedIds.size > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="font-medium">
                {selectedIds.size} attività selezionate
              </span>
              <Select value={bulkAssignUserId} onValueChange={setBulkAssignUserId}>
                <SelectTrigger className="w-[200px]">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Assegna a..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleBulkAssign}
                disabled={!bulkAssignUserId}
              >
                Assegna
              </Button>
              <Button
                variant="outline"
                onClick={() => setSelectedIds(new Set())}
              >
                Annulla selezione
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {loading ? 'Caricamento...'
              : viewMode === 'grouped' && pagination
                ? `${pagination.total} pratic${pagination.total !== 1 ? 'he' : 'a'} con ${pagination.totalActivities ?? activities.length} attività`
                : pagination
                  ? `${pagination.total} attività${pagination.total !== 1 ? ' trovate' : ' trovata'}`
                  : `${activities.length} attività${activities.length !== 1 ? ' trovate' : ' trovata'}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && activities.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Caricamento attività...</p>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">Nessuna attività trovata</p>
              <Button onClick={() => navigate('/activity-types')}>
                <Plus className="h-4 w-4 mr-2" />
                Crea Prima Attività
              </Button>
            </div>
          ) : viewMode === 'grouped' ? (
            <PracticeActivityGroups
              groups={groupedByPractice}
              expandedPractices={expandedPractices}
              onTogglePractice={togglePracticeExpand}
              practiceBulkSelect={
                filterMode === 'unassigned'
                  ? {
                      enabled: true,
                      selectedIds,
                      onToggleGroup: (activityIds, checked) => {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (checked) activityIds.forEach((id) => next.add(id));
                          else activityIds.forEach((id) => next.delete(id));
                          return next;
                        });
                      },
                    }
                  : undefined
              }
              renderActivity={(activity) => (
                <ActivityCard
                  activity={activity}
                  users={users}
                  onAssign={(userId) => handleAssign(activity.id, userId)}
                  onDetailClick={() => handleActivityClick(activity)}
                  showCheckbox={showCheckboxes}
                  selected={selectedIds.has(activity.id)}
                  onSelectChange={(sel) => handleSelectChange(activity.id, sel)}
                  showStatusActions={filterMode === 'mine'}
                  onStatusChange={(status) => handleStatusChange(activity.id, status)}
                  actionType={(() => {
                    const at = activityTypes.find((t) => t.code === activity.type);
                    if (at?.actionType) return at.actionType as 'file' | 'form';
                    if (at?._count?.modules && at._count.modules > 0) return 'form';
                    return 'file';
                  })()}
                  onActionClick={() => {
                    const at = activityTypes.find((t) => t.code === activity.type);
                    const eff =
                      at?.actionType || (at?._count?.modules && at._count.modules > 0 ? 'form' : 'file');
                    setActionActivity(activity);
                    setActionMode(eff as 'file' | 'form');
                  }}
                />
              )}
            />
          ) : viewMode === 'list' ? (
            <div className="space-y-3">
              {activities.map((activity) => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  users={users}
                  onAssign={(userId) => handleAssign(activity.id, userId)}
                  onDetailClick={() => handleActivityClick(activity)}
                  showCheckbox={showCheckboxes}
                  selected={selectedIds.has(activity.id)}
                  onSelectChange={(sel) => handleSelectChange(activity.id, sel)}
                  showStatusActions={filterMode === 'mine'}
                  onStatusChange={(status) => handleStatusChange(activity.id, status)}
                  actionType={(() => {
                    const at = activityTypes.find(t => t.code === activity.type);
                    if (at?.actionType) return at.actionType as 'file' | 'form';
                    if (at?._count?.modules && at._count.modules > 0) return 'form';
                    return 'file';
                  })()}
                  onActionClick={() => {
                    const at = activityTypes.find(t => t.code === activity.type);
                    const eff = at?.actionType || (at?._count?.modules && at._count.modules > 0 ? 'form' : 'file');
                    setActionActivity(activity);
                    setActionMode(eff as 'file' | 'form');
                  }}
                />
              ))}
            </div>
          ) : (
            <ActivityKanban
              activities={activities}
              users={users}
              activityTypes={activityTypes}
              onAssign={(activityId, userId) => handleAssign(activityId, userId)}
              onActivityClick={handleActivityClick}
              onActionClick={(activity, mode) => {
                setActionActivity(activity);
                setActionMode(mode);
              }}
              showCheckbox={showCheckboxes}
              selectedIds={selectedIds}
              onSelectChange={handleSelectChange}
              showStatusActions={filterMode === 'mine'}
              onStatusChange={handleStatusChange}
            />
          )}

          {pagination && pagination.total > 10 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={pagination.page <= 1}
                >
                  Precedente
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  Successiva
                </Button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-700">
                    Mostrando{' '}
                    <span className="font-medium">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>{' '}
                    a{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    di <span className="font-medium">{pagination.total}</span> {viewMode === 'grouped' ? 'pratiche' : 'risultati'}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">{viewMode === 'grouped' ? 'Pratiche' : 'Elementi'} per pagina:</span>
                    <Select
                      value={itemsPerPage.toString()}
                      onValueChange={(value) => setItemsPerPage(parseInt(value))}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={pagination.page <= 1}
                      className="rounded-l-md"
                    >
                      Precedente
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="rounded-r-md"
                    >
                      Successiva
                    </Button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {actionActivity && actionMode === 'file' && (
        <ActivityFileDropzone
          open={true}
          onClose={() => { setActionActivity(null); setActionMode(null); }}
          activityTitle={actionActivity.title}
          practiceId={actionActivity.practiceId}
          practiceDocumentPath={actionActivity.practice?.documentPath ?? undefined}
          subfolder={actionActivity.type}
          activityId={actionActivity.id}
          onUploadSuccess={async () => {
            if (actionActivity?.id) {
              await completeActivity(actionActivity.id);
              setActionActivity(null);
              setActionMode(null);
              fetchActivities(buildFetchParams());
            }
          }}
        />
      )}

      {actionActivity && actionMode === 'form' && (
        <ActivityFormDialog
          open={true}
          onClose={() => { setActionActivity(null); setActionMode(null); }}
          activityTitle={actionActivity.title}
          activityTypeCode={actionActivity.type}
          practiceId={actionActivity.practiceId}
          practiceDocumentPath={actionActivity.practice?.documentPath ?? undefined}
          subfolder={actionActivity.type}
          moduleId={activityTypes.find(t => t.code === actionActivity.type)?.moduleId}
        />
      )}
    </div>
  );
}
